import { NebulaWebGameSDK, SDKEnv, SDKStatusCode } from "@nebula-sdk/compose";
import { NebulaSiteService } from "./core";
import message from "./message";
import type {
  SDKInitArgs, SDKInitResult, SDKLoginResult, SDKPayArgs, SDKPayResult, SDKPlatform, SDKRequestOptions,
  SDKResult,
} from "@nebula-sdk/compose";
import type { App } from "vue";

export class NebulaSiteDirectService {
  public constructor() {
    this.core = new NebulaSiteService();
    this.sdk = new NebulaWebGameSDK();
  }

  private readonly core: NebulaSiteService;

  private readonly sdk: NebulaWebGameSDK;

  public get token(): string | null {
    return this.core.token;
  }

  public get platform(): SDKPlatform {
    return this.core.platform;
  }

  private success(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message !== true || !args.result.msg) return;
    message.info({ message: args.result.msg ?? "成功" });
  }

  private error(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message === false || !args.result.msg) return;
    message.info({ message: args.result.msg ?? "错误" });
  }

  // 日志上报
  public async scopeRequestTrack(args: {
    eventGroup?: string;
    eventLevel?: "info" | "warn" | "error";
    eventId: string;
    eventName: string;
    eventValue: Record<string, unknown>;
  }): Promise<SDKResult<null>> {
    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };

    return await this.core.track({
      appId: this.core.options?.config.nbAppId ?? "",
      appVersion: this.core.version,
      appChannel: SDKEnv.Web,
      userId: this.core.userInfo?.nbOpenId ?? "",
      userTag: this.core.userInfo?.openId ?? "",
      deviceId: this.core.deviceId,
      deviceModel: navigator.userAgentData?.platform ?? "unknown",
      osType: this.core.platform,
      osVersion: navigator.userAgent,
      networkType: navigator.connection?.effectiveType ?? "unknown",
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.core.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  public async scopeRequestPassport<T = any>(
    args: SDKRequestOptions & { message?: boolean },
  ): Promise<SDKResult<T>> {
    const result = await this.core.requestPassport<T>({ ...args });

    if (result.status === "success") {
      this.success({ message: args.message, result });
    } else {
      this.error({ message: args.message, result });
    }

    void this.scopeRequestTrack({
      eventId: args.url,
      eventName: "通行证请求",
      eventGroup: "web-direct-site",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    const result = await this.core.init(args);
    if (result.status === "error") return result;
    return await this.sdk.init(args);
  }

  public async login(): Promise<SDKLoginResult> {
    return await this.sdk.login();
  }

  public async logout(): Promise<SDKResult<null>> {
    this.core.userInfo = null;
    return await Promise.resolve({
      status: "success",
      code: SDKStatusCode.Success,
      msg: "退出登录成功",
      data: null,
    });
  }

  public async pay(args: SDKPayArgs): Promise<SDKPayResult> {
    return await this.sdk.pay(args);
  }

  public install(app: App): void {
    Object.defineProperty(app.config.globalProperties, "$direct", {
      enumerable: true,
      get: () => this,
    });
  }
}

export default new NebulaSiteDirectService();
